import React, { useEffect, useState } from 'react'
import {
  type AccountDataSelection as AccountData,
  LoadingTypes,
  isLoading as Loading,
} from '@labrav/common'
import { useDispatch, useSelector } from '../../store'
import CombinedFormHeader from './CombinedFormHeader'
import { getProducts } from '../../modules/productsListing/action'
import { useStyles } from './CombinedForm.styles'
import { ButtonV2 } from '@labrav/react-components'
import { Form, Formik } from 'formik'
import { FooterV2 } from '../../common/components/FooterV2/FooterV2'
import { CombinedFormState } from '../../modules/combinedForm/reducer'
import { FormSections } from './FormSections/FormSections'
import { Product } from '../../modules/productsListing/reducer'
import { isEmpty } from 'lodash'
import { saveCombinedFormData } from '../../modules/combinedForm/action'
import { useParams } from 'react-router-dom'
import { getFlyoutListingData } from '../../modules/flyOutSyncs/action'
import { FlyOutListingData } from '../../types/flyout'
import { CircularProgress } from '@mui/material'
import { getCombinedFormikValidationSchema } from '../../common/utils/combinedFormValidationSchema'

export type CombinedFormProps = {
  isExternal?: boolean
}

const CombinedForm: React.FC<CombinedFormProps> = () => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const queryParams = new URLSearchParams(location.search)
  const labraIsvId = queryParams.get('labra_isv_id') as string
  const viewingPersona = queryParams.get('viewing_persona') as string
  const labraProductID = queryParams.get('labra_product_id') as string

  const { labraFormId: labraFormId } = useParams<{
    labraFormId: string
    partnerId: string
  }>()
  const accountId = labraIsvId
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const accountData = useSelector<AccountData[]>(state =>
    ((state.accountSelection?.availableAccounts || []) as AccountData[]).filter(
      account =>
        account?.cloud === 'AWS' && account?.product === ('FLYOUT' as string)
    )
  )
  const combinedFormData = useSelector<CombinedFormState>(
    state => state.combinedFormInCreation || ({} as CombinedFormState)
  )
  const flyoutSellerInfo =
    useSelector<FlyOutListingData | undefined>(
      state => state.flyoutSellerInfo
    ) ?? ({} as FlyOutListingData)
  const vendorName = flyoutSellerInfo.awsAccountName ?? ''
  const handleProductSelect = (product: Product) => {
    setSelectedProduct(product)
  }
  useEffect(() => {
    dispatch(getProducts(accountId, 'AWS'))
  }, [accountId])
  const products = useSelector<Product[]>(
    state => state.productsListing['AWS']?.products?.rows || []
  )
  useEffect(() => {
    if (accountId) {
      dispatch(
        getFlyoutListingData({
          partnerId: accountId,
          metadata: true,
        })
      )
    }
  }, [accountId])

  useEffect(() => {
    if (
      combinedFormData?.resellerAgreementData?.awsProductId &&
      products.length
    ) {
      const found = products.find(
        p => p.productId === combinedFormData.resellerAgreementData.awsProductId
      )
      if (found) setSelectedProduct(found)
    }
  }, [combinedFormData, products])
  const isLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.COMBINED_FORM_LOADING)
  )
  const isProductsLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.FETCH_PRODUCTS)
  )
  if (isLoading || isProductsLoading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress size={30} />
      </div>
    )
  }
  const sendCombinedFormData = async (data: CombinedFormState) => {
    await dispatch(
      saveCombinedFormData(
        data,
        selectedProduct?.productType,
        sendFormSavedMessage
      )
    )
  }
  const validationSchema = getCombinedFormikValidationSchema(
    selectedProduct?.productType as string
  )

  let disabled = false
  if (viewingPersona && viewingPersona === 'ISV') {
    disabled = true
  }
  const eulaUrl = selectedProduct?.eulaUrl
    ? selectedProduct.eulaUrl
    : 'https://s3.amazonaws.com/aws-mp-standard-contracts/Standard-Contact-for-AWS-Marketplace-2022-07-14.pdf'

  const sendFormSavedMessage = (
    labraResellerId: string,
    labraIsvId: string,
    combinedFormId: string,
    externalId: string
  ) => {
    if (typeof document !== 'undefined' && document.referrer) {
      try {
        const targetOrigin = new URL(document.referrer).origin
        window.parent.postMessage(
          {
            type: 'LABRA_FORM_SAVED',
            payload: {
              labra_reseller_id: labraResellerId,
              labra_isv_id: labraIsvId,
              combined_form_id: combinedFormId,
              external_id: externalId,
            },
          },
          targetOrigin
        )
      } catch {
        console.error('Invalid document.referrer URL:', document.referrer)
      }
    } else {
      console.error(
        'Cannot determine parent origin - referrer is not available.'
      )
    }
  }

  return (
    <div>
      <Formik
        initialValues={combinedFormData}
        validationSchema={validationSchema}
        validateOnMount={true}
        onSubmit={() => void 0}
        enableReinitialize
      >
        {formikProps => {
          const handleSaveClick = async () => {
            await formikProps.submitForm()
            if (Object.keys(formikProps.errors).length > 0) {
              return
            }
            sendCombinedFormData(formikProps.values)
          }
          useEffect(() => {
            formikProps.setFieldValue('labraIsvId', labraIsvId)
            formikProps.setFieldValue(
              'cppoData.cpInfo.awsAccountId',
              flyoutSellerInfo.awsAccountId
            )
            formikProps.setFieldValue(
              'resellerAgreementData.isvName',
              vendorName
            )
            if (labraFormId) {
              formikProps.setFieldValue('id', labraFormId)
            }
          }, [labraIsvId, flyoutSellerInfo, vendorName])

          useEffect(() => {
            if (labraProductID && products.length) {
              const productItem = products.find(
                p => p.productId === labraProductID
              )
              formikProps.setFieldValue(
                'resellerAgreementData.awsProductId',
                labraProductID
              )
              if (productItem) {
                formikProps.setFieldValue(
                  'resellerAgreementData.productType',
                  productItem.productType
                )
                formikProps.setFieldValue(
                  'resellerAgreementData.cloudProductUid',
                  productItem.awsProductId
                )
              }
            }
          }, [labraProductID, products])
          return (
            <Form>
              <div className={classes.combinedFormRoot}>
                <CombinedFormHeader
                  accounts={accountData}
                  vendorName={vendorName}
                  onSelect={handleProductSelect}
                  // disableProductSelection={!isEmpty(labraFormId)}
                  disabled={disabled}
                />
                {formikProps.values.resellerAgreementData.awsProductId && (
                  <FormSections
                    offset={160}
                    productType={selectedProduct?.productType}
                    eulaUrl={eulaUrl}
                    disabled={disabled}
                    labraFormId={labraFormId}
                    flyoutSellerInfo={flyoutSellerInfo}
                  />
                )}
              </div>
              <FooterV2
                actions={
                  <div className={classes.footerWrapper}>
                    <div className={classes.footerWrapperButtons}>
                      <ButtonV2
                        styleType="primary"
                        onClick={handleSaveClick}
                        data-testid={'save-as-draft'}
                        loading={false}
                        disabled={
                          disabled ||
                          isEmpty(formikProps.values.labraCpId) ||
                          isEmpty(
                            formikProps.values.resellerAgreementData
                              .awsProductId
                          )
                        }
                      >
                        {'Save as draft'}
                      </ButtonV2>
                    </div>
                  </div>
                }
                width={'-webkit-fill-available'}
                customClass={classes.customFooter}
              />
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}
export default CombinedForm
